import { useLang } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import ServiceLinksBlock from "../components/ServiceLinksBlock";

// TODO: replace with the actual María y José Luis photo URL from ls39motos.com
const DEALER_IMG = "https://ls39motos.com/wp-content/uploads/2025/04/ls-39-motos-equipo-02.jpeg";
const SECTION_IMG = "https://ls39motos.com/wp-content/uploads/2025/04/ls-39-motos-jose-luis-y-maria.jpg";

export default function Dealership() {
    const { lang } = useLang();

    const t = lang === "es" ? {
        overline: "Concesionario Oficial Yamaha · Mallorca",
        title: "El origen de una Alianza",
        subtitle: "cuando Yamaha llamó a nuestra puerta",
        intro: "Toda gran historia comienza con una idea valiente. En el caso de LS39 Motos, esa chispa surgió de la mano de la propia marca Yamaha, que vio en la familia de Luis Salom a los embajadores perfectos para representar su excelencia, su ingeniería y su espíritu Kando en la isla de Mallorca.",
        quote_title: "«La idea era bonita y tiramos adelante»",
        quote_p1: "Con esta sencillez y determinación explican María y José Luis (padres de Luis y pilares del proyecto) el momento en que se gestó lo que hoy es nuestro concesionario. No se trataba solo de abrir un negocio más; era el desafío de crear un centro de referencia que estuviera a la altura de una marca japonesa líder mundial y, al mismo tiempo, honrara una trayectoria vital ligada siempre al asfalto.",
        quote_p2: "La propuesta de Yamaha no fue casual. Buscaban pasión, conocimiento del sector y, sobre todo, una conexión humana que solo una familia que ha respirado motociclismo toda su vida podía ofrecer.",
        commitment_title: "Un compromiso oficial con Mallorca",
        commitment_p1: "Ser el Concesionario Oficial Yamaha en Palma es un sello de garantía que nos obliga a la mejora continua. Desde que aceptamos aquel reto, nuestro objetivo ha sido claro: trasladar la precisión de la tecnología japonesa al servicio cercano y profesional que demandan los motoristas mallorquines.",
        commitment_p2: "En nuestras instalaciones del Polígono de Son Castelló, no solo encontrarás los últimos modelos de la marca, sino la materialización de aquella «idea bonita» que María y José Luis decidieron impulsar. Hoy, LS39 Motos es el punto de encuentro donde la visión global de Yamaha y la pasión local de nuestra familia se unen para ofrecerte lo mejor del mundo de las dos ruedas.",
        cta_btn: "Ver nuestra gama Yamaha",
    } : {
        overline: "Official Yamaha Dealership · Mallorca",
        title: "The origin of an Alliance",
        subtitle: "when Yamaha knocked on our door",
        intro: "Every great story begins with a bold idea. In the case of LS39 Motos, that spark came from the Yamaha brand itself, which saw in Luis Salom's family the perfect ambassadors to represent its excellence, engineering and Kando spirit on the island of Mallorca.",
        quote_title: "«It was a beautiful idea and we went for it»",
        quote_p1: "With this simplicity and determination, María and José Luis (Luis's parents and the pillars of the project) describe the moment in which what is today our dealership was born. It wasn't just about opening another business; it was the challenge of creating a reference centre that would live up to a world-leading Japanese brand and, at the same time, honour a lifetime journey always linked to the tarmac.",
        quote_p2: "Yamaha's proposal was no accident. They were looking for passion, industry knowledge and, above all, a human connection that only a family who had breathed motorcycling all their lives could offer.",
        commitment_title: "An official commitment to Mallorca",
        commitment_p1: "Being the Official Yamaha Dealership in Palma is a quality seal that demands continuous improvement. Since we accepted that challenge, our objective has been clear: to bring the precision of Japanese technology to the close, professional service that Mallorcan riders demand.",
        commitment_p2: "In our Polígono de Son Castelló facilities, you will not only find the latest models from the brand, but the realisation of that «beautiful idea» that María and José Luis decided to pursue. Today, LS39 Motos is the meeting point where Yamaha's global vision and our family's local passion come together to offer you the best of the two-wheeled world.",
        cta_btn: "View our Yamaha range",
    };

    return (
        <div data-testid="dealership-page">
            {/* HERO */}
            <section className="relative isolate overflow-hidden border-b border-black/10">
                <div className="absolute inset-0 -z-10">
                    <img src={DEALER_IMG} alt="LS39 Motos" className="h-full w-full object-cover opacity-40" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[--ls-bg]/60 via-[--ls-bg]/40 to-[--ls-bg]" />
                </div>
                <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
                    <p className="text-overline">{t.overline}</p>
                    <h1 className="mt-6 font-display text-5xl font-bold tracking-tighter sm:text-6xl lg:text-7xl">
                        {t.title}
                        <br />
                        <span style={{ color: "var(--ls-terracotta)" }}>{t.subtitle}</span>
                    </h1>
                </div>
            </section>

            {/* INTRO */}
            <section className="mx-auto max-w-4xl px-6 py-20 lg:px-10 lg:py-28">
                <p className="text-base text-neutral-700 leading-relaxed sm:text-lg">{t.intro}</p>
            </section>

            {/* QUOTE + IMAGE */}
            <section className="border-y border-black/10 bg-[--ls-paper]">
                <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
                    <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                        <div className="lg:col-span-5">
                            <div className="relative aspect-[4/5] overflow-hidden border border-black/10">
                                <img
                                    src={SECTION_IMG}
                                    alt="María y José Luis"
                                    className="h-full w-full object-cover"
                                />
                            </div>
                        </div>
                        <div className="lg:col-span-7 space-y-6 flex flex-col justify-center">
                            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                                {t.quote_title}
                            </h2>
                            <p className="text-base text-neutral-700 leading-relaxed">{t.quote_p1}</p>
                            <p className="text-base text-neutral-700 leading-relaxed">{t.quote_p2}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* COMMITMENT */}
            <section className="mx-auto max-w-4xl px-6 py-20 lg:px-10 space-y-6">
                <h2 className="font-display text-3xl font-bold tracking-tight">{t.commitment_title}</h2>
                <p className="text-base text-neutral-700 leading-relaxed">{t.commitment_p1}</p>
                <p className="text-base text-neutral-700 leading-relaxed">{t.commitment_p2}</p>
                <div className="pt-4">
                    <Link to="/motos" className="btn-primary">
                        {t.cta_btn} <ArrowRight size={16} />
                    </Link>
                </div>
            </section>

            {/* SERVICE LINKS */}
            <ServiceLinksBlock />
        </div>
    );
}
